import { useCallback, useEffect, useRef, useState } from 'react';
import type { WireChatMessage } from '@logistics/contracts';
import { api } from '../client';
import { failureOf } from '@/lib/result';
import { useToast } from '../shell/toast';

export type AttachmentRef = WireChatMessage['attachments'][number];

/** A chip in the composer: uploaded (with its id), or still uploading. */
export interface ComposerFile {
  readonly key: string;
  readonly name: string;
  readonly ref?: AttachmentRef;
}

export const MAX_ATTACHMENTS = 3;
const MAX_BYTES = 10 * 1024 * 1024;
export const ACCEPT = '.pdf,.txt,.csv,.md,application/pdf,text/plain,text/csv,text/markdown';

/**
 * Files for the next question, uploaded as soon as they are picked so the
 * server has extracted their text by the time the question is sent. The
 * server's refusal — too big, the wrong type, no text in it — is a toast.
 */
export function useAttachments(): {
  files: ComposerFile[];
  uploading: boolean;
  add: (picked: readonly File[]) => void;
  remove: (key: string) => void;
  clear: () => void;
  refs: () => AttachmentRef[];
} {
  const toast = useToast();
  const [files, setFiles] = useState<ComposerFile[]>([]);
  const count = useRef(0);
  const serial = useRef(0);
  useEffect(() => {
    count.current = files.length;
  }, [files]);

  const upload = useCallback(
    async (file: File, key: string): Promise<void> => {
      const drop = (): void => setFiles((all) => all.filter((f) => f.key !== key));
      try {
        const r = await api.chat.attach({ body: { file } });
        if (r.status === 201) {
          const { id, name, mime, size } = r.body.attachment;
          setFiles((all) =>
            all.map((f) => (f.key === key ? { ...f, ref: { id, name, mime, size } } : f)),
          );
          return;
        }
        drop();
        toast(failureOf(r).detail ?? `Could not attach ${file.name} (${r.status})`);
      } catch {
        drop();
        toast(`Could not attach ${file.name}: the API did not answer`);
      }
    },
    [toast],
  );

  const add = useCallback(
    (picked: readonly File[]) => {
      const room = MAX_ATTACHMENTS - count.current;
      if (picked.length > room) toast(`Up to ${MAX_ATTACHMENTS} files per question`);
      const next: ComposerFile[] = [];
      for (const file of picked.slice(0, Math.max(0, room))) {
        if (file.size > MAX_BYTES) {
          toast(`${file.name} is over 10 MB`);
          continue;
        }
        serial.current += 1;
        next.push({ key: `${serial.current}-${file.name}`, name: file.name });
        void upload(file, `${serial.current}-${file.name}`);
      }
      count.current += next.length;
      setFiles((current) => [...current, ...next]);
    },
    [toast, upload],
  );

  return {
    files,
    uploading: files.some((f) => f.ref === undefined),
    add,
    remove: (key) => setFiles((all) => all.filter((f) => f.key !== key)),
    clear: () => setFiles([]),
    refs: () => files.flatMap((f) => (f.ref === undefined ? [] : [f.ref])),
  };
}
